import { isEmpty } from 'lodash';

import { type DiscussionAnnotationType } from '@air/analytics';
import { type Annotation, type BoxAnnotation } from '@air/api/types';

import { type Point } from '~/components/BoundingBox/types';

export type PencilAnnotationRect = {
  minX: number;
  maxX: number;
  maxY: number;
};

const calculatePencilAnnotationRect = (paths: Point[][]): PencilAnnotationRect => {
  let minX = Number.MAX_VALUE;
  let maxX = 0;
  let maxY = 0;
  paths.forEach((path) => {
    path.forEach(({ y, x }) => {
      if (x < minX) minX = x;
      if (x > maxX) {
        maxX = x;
        maxY = y;
      }
    });
  });

  return {
    minX,
    maxX,
    maxY,
  };
};

export const isAnnotationCorrect = (annotation?: Annotation): annotation is Annotation => {
  if (!annotation || isEmpty(annotation)) {
    return false;
  } else if (!annotation.type || annotation.type === 'box') {
    return !!annotation.coordinates && !!annotation.dimensions;
  } else if (annotation.type === 'pencil') {
    return annotation.lines?.length > 0 && !!annotation.lines[0].coordinates;
  }
  return false;
};

/**
 * V2: Check if an annotations array contains valid annotations
 * @param annotations - Array of annotations to validate
 */
export const isAnnotationArrayCorrect = (annotations?: Annotation[]): boolean => {
  if (!annotations || !Array.isArray(annotations) || annotations.length === 0) {
    return false;
  }
  // At least one annotation must be valid
  return annotations.some((annotation) => isAnnotationCorrect(annotation));
};

/**
 * V2: Get the first valid annotation from an annotations array
 * @param annotations - Array of annotations
 * @returns First valid annotation or undefined
 */
export const getFirstValidAnnotation = (annotations?: Annotation[]): Annotation | undefined => {
  if (!annotations || !Array.isArray(annotations)) {
    return undefined;
  }
  return annotations.find((annotation) => isAnnotationCorrect(annotation));
};

/**
 * V2: Calculate comment position using the first annotation in the annotations array
 * Positions the comment input based on the primary annotation
 * @param annotations - Array of annotations (V2 format)
 */
export const getAnnotationCommentPos = (annotations?: Annotation[]) => {
  const firstAnnotation = getFirstValidAnnotation(annotations);

  if (!firstAnnotation || !isAnnotationCorrect(firstAnnotation)) {
    return {
      minX: 0,
      maxX: 0,
      maxY: 0,
    };
  }

  switch (firstAnnotation?.type) {
    case 'pencil':
      return calculatePencilAnnotationRect(firstAnnotation.lines.map(({ coordinates }) => coordinates.map((c) => c)));
    case 'box':
    case undefined:
      return {
        minX: firstAnnotation.coordinates.x,
        maxX: firstAnnotation.dimensions.width, // V1 compatibility: dimensions.width is bottom-right X
        maxY: firstAnnotation.dimensions.height, // V1 compatibility: dimensions.height is bottom-right Y
      };
    default:
      return { minX: 0, maxX: 0, maxY: 0 };
  }
};

export const isPointAnnotation = ({ dimensions, coordinates }: Pick<BoxAnnotation, 'coordinates' | 'dimensions'>) =>
  coordinates.x === dimensions.width && coordinates.y === dimensions.height; // V1: point annotations have same top-left and bottom-right

/**
 * V2: Get page number from annotations array (uses first valid annotation)
 * @param annotations - Array of annotations
 */
export const getPageNumber = (annotations?: Annotation[]) => {
  const firstAnnotation = getFirstValidAnnotation(annotations);

  if (!firstAnnotation) {
    return undefined;
  }

  switch (firstAnnotation?.type) {
    case 'pencil':
      return firstAnnotation.lines[0]?.pageNumber;
    case 'box':
    case undefined:
      return firstAnnotation?.pageNumber;
  }
};

/**
 * V2: Check if discussion has valid annotations (uses annotations array)
 * @param discussion - Discussion with annotations array
 */
export const hasAnnotation = (discussion: { annotations?: Annotation[] }) =>
  isAnnotationArrayCorrect(discussion.annotations);

/**
 * V2: Get analytics type from annotations array (uses first valid annotation)
 * @param annotations - Array of annotations
 */
export const getAnnotationAnalyticsType = (annotations?: Annotation[]): DiscussionAnnotationType => {
  const firstAnnotation = getFirstValidAnnotation(annotations);

  if (!isAnnotationCorrect(firstAnnotation)) {
    return 'standalone';
  } else {
    if (firstAnnotation.type === 'pencil') return 'pencil';
    else if (isPointAnnotation(firstAnnotation)) return 'point';
    else return 'area';
  }
};
